/// <amd-module name="Wiki/Mermaid/MermaidRenderer" />
import * as Mermaid from "mermaid";
import * as React from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { getId } from "VSS/Core/Util/Object";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { getFocusVisible } from "VSSUI/Util";
import { MermaidTheme } from "Wiki/Mermaid/Constants";
import {
    contentOverflowsContainer,
    getMermaidTheme,
    isIE,
    isMermaidRenderExtraFunctionEnabled,
    isNewMermaidDiagramSupported,
    isSupportedDiagram,
    mermaidInitialize,
    mermaidRender
} from "Wiki/Mermaid/Helpers";
import * as Resources from "Wiki/Mermaid/Resources";
export interface MermaidRendererProps {
    content: string;
    isEditMode: boolean;
}

export interface MermaidRendererState {
    svgCodeForContent?: string;
    mermaidError?: MermaidError;
    theme: MermaidTheme;
}

const enum ErrorType {
    ParseFailure,
    InvalidDiagram,
    UnSupportedBrowser
}

interface MermaidError {
    errorMessage: string;
    errorType: ErrorType;
}

export class MermaidRenderer extends VssComponent<MermaidRendererProps, MermaidRendererState> {
    private _containerRef = React.createRef<HTMLDivElement>();
    private _parseFailureMsgContainerRef = React.createRef<HTMLPreElement>();

    private _themeService: IVssThemeService;
    private _isNewDiagramSupported: boolean = false;
    private _isMermaidRenderExtraFunctionEnabled: boolean = false;

    constructor(props: MermaidRendererProps, context: IVssComponentContext) {
        super(props, context);

        this._themeService = context.pageContext.getService<IVssThemeService>("IVssThemeService");
        this._isNewDiagramSupported = isNewMermaidDiagramSupported(this.context.pageContext);
        this._isMermaidRenderExtraFunctionEnabled = isMermaidRenderExtraFunctionEnabled(this.context.pageContext);
        const theme = getMermaidTheme(this._themeService);
        mermaidInitialize(theme);
        let mermaidError: MermaidError | undefined;
        if (isIE()) {
            mermaidError = {
                errorMessage: Resources.IE_NotSupportedMessage,
                errorType: ErrorType.UnSupportedBrowser
            };
        }
        if (this.props.content && !isSupportedDiagram(this.props.content, this._isNewDiagramSupported)) {
            mermaidError = {
                errorMessage: Resources.Diagram_NotSupportedMessage,
                errorType: ErrorType.InvalidDiagram
            };
        }

        this.state = { mermaidError, theme };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._themeService.subscribe(this._onThemeChanged, "themeChanged");

        if (this.props.content && !this.state.mermaidError) {
            this._renderMermaidContent();
        }

        if (this.props.isEditMode && this.state.mermaidError) {
            announce(format(Resources.Diagram_LoadingFailedMessage, this.state.mermaidError.errorMessage), true);
        }
    }

    public componentDidUpdate(prevProps: Readonly<MermaidRendererProps>, prevState: Readonly<MermaidRendererState>): void {
        super.componentDidUpdate(prevProps, prevState);

        const themeChanged = this.state.theme !== prevState.theme;
        const contentChanged = this.props.content !== prevProps.content;
        if (themeChanged || contentChanged) {
            if (themeChanged) {
                mermaidInitialize(this.state.theme);
            }
            this._renderMermaidContent();
        }

        if (this.props.isEditMode) {
            if (this.state.svgCodeForContent && this.state.svgCodeForContent !== prevState.svgCodeForContent) {
                announce(Resources.Diagram_LoadingSucceededMessage, true);
            } else if (this.state.mermaidError && this.state.mermaidError !== prevState.mermaidError) {
                announce(format(Resources.Diagram_LoadingFailedMessage, this.state.mermaidError.errorMessage), true);

                if (
                    this._parseFailureMsgContainerRef.current &&
                    getFocusVisible() &&
                    contentOverflowsContainer(this._parseFailureMsgContainerRef.current)
                ) {
                    this._parseFailureMsgContainerRef.current.focus();
                }
            }
        }
    }

    public render(): JSX.Element {
        return (
            <div className={"wiki-mermaid-container padding-bottom-8"}>
                {this.state.mermaidError ? (
                    this._renderErrorMessage(this.state.mermaidError)
                ) : (
                    <div className="mermaid" ref={this._containerRef} dangerouslySetInnerHTML={{ __html: this.state.svgCodeForContent || "" }} />
                )}
            </div>
        );
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this._themeService.unsubscribe(this._onThemeChanged, "themeChanged");
    }

    private _renderMermaidContent = (): void => {
        if (this.props.content) {
            if (isSupportedDiagram(this.props.content, this._isNewDiagramSupported)) {
                try {
                    // mermaid.render creates a div inside <body> element in the DOM, where it temporarily renders the svg element for the input diagram content.
                    // Once temp rendering is done it removes the div element. This creates problems as the newly created div element is created at the end of
                    // the document body and has very less width available due to the flex display settings for <body> tag in our pages.
                    //
                    // render() optionally accepts a param to pass the container element inside which it can temporarily render the svg content.
                    // This helps us in providing a temp container which has fuller width than it is getting using the temp container approach.
                    // Passing the current container html element to avoid the rendering issues

                    if(this._isMermaidRenderExtraFunctionEnabled) { 
                        mermaidRender(
                            getId("mermaid-diagram"), 
                            this.props.content || "",
                            (svgCode: string) => {
                                this.setState({ svgCodeForContent: svgCode, mermaidError: undefined });
                            },
                            this._containerRef && this._containerRef.current ? this._containerRef.current : undefined);
                    }
                    else { 
                        Mermaid.render(
                            getId("mermaid-diagram"),
                            this.props.content || "",
                            (svgCode: string) => {
                                this.setState({ svgCodeForContent: svgCode, mermaidError: undefined });
                            },
                            this._containerRef && this._containerRef.current ? this._containerRef.current : undefined
                        );
                    }
                } catch (error) {
                    this.setState({ mermaidError: { errorMessage: error.message, errorType: ErrorType.ParseFailure }, svgCodeForContent: undefined });
                }
            } else {
                this.setState({
                    mermaidError: { errorMessage: Resources.Diagram_NotSupportedMessage, errorType: ErrorType.InvalidDiagram },
                    svgCodeForContent: undefined
                });
            }
        }
    };

    private _renderErrorMessage = (mermaidError: MermaidError): JSX.Element => {
        if (mermaidError.errorType === ErrorType.ParseFailure) {
            return (
                <pre ref={this._parseFailureMsgContainerRef} tabIndex={0} aria-label={Resources.ParseFailureMessageContainerAriaLabel}>
                    {mermaidError.errorMessage}
                </pre>
            );
        } else {
            return (
                <div className="flex-row flex-center">
                    <Status className="icon-margin" size={StatusSize.m} {...Statuses.Warning} />
                    <div className="secondary-text"> {mermaidError.errorMessage} </div>
                </div>
            );
        }
    };

    private _onThemeChanged = (): void => {
        this.setState({ theme: getMermaidTheme(this._themeService) });
    };
}

VssComponent.register("Wiki.MermaidComponent", MermaidRenderer);
